import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Heart, Share2, Radio, Users, Bell, Gift, Send, MoreVertical } from "lucide-react";
import api from "@/lib/api";

const MOCK_CHAT = [
  { id: 1, user: "aria.mp3", color: "hsl(190,92%,55%)", text: "Que jogada!" },
  { id: 2, user: "krab_02", color: "hsl(217,96%,60%)", text: "GG GG 🔥" },
  { id: 3, user: "vy.on", color: "hsl(300,92%,62%)", text: "Como faz esse combo?" },
  { id: 4, user: "sombra", color: "hsl(190,92%,55%)", text: "Chegando agora, o que perdi?" },
  { id: 5, user: "nyx", color: "hsl(40,96%,56%)", text: "streamer top 👑" },
  { id: 6, user: "beto", color: "hsl(145,62%,55%)", text: "mapa novo tá insano" },
  { id: 7, user: "mel", color: "hsl(217,96%,60%)", text: "bora rush B?" },
];

export default function StreamPage() {
  const { streamId } = useParams();
  const [stream, setStream] = useState(null);

  useEffect(() => {
    api.get(`/streams/${streamId}`).then((r) => setStream(r.data)).catch(() => setStream(false));
  }, [streamId]);

  if (stream === null) return <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm font-mono">carregando live…</div>;
  if (stream === false) return <div className="flex-1 flex items-center justify-center text-muted-foreground">Live não encontrada.</div>;

  return (
    <main className="flex-1 min-w-0 flex overflow-hidden" data-testid="stream-page">
      <section className="flex-1 min-w-0 overflow-y-auto scrollbar-thin">
        {/* Player */}
        <div className="p-4 lg:p-6">
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video border border-[hsl(var(--nx-border))] nx-glow-blue">
            <video
              key={stream.video_url}
              className="w-full h-full object-cover"
              src={stream.video_url}
              autoPlay muted loop controls playsInline
              data-testid="stream-player"
            />
            <div className="absolute top-3 left-3 nx-chip nx-chip-red !py-1 !px-2.5">
              <span className="nx-live-dot" style={{background:'#fff'}} /> AO VIVO
            </div>
            <div className="absolute top-3 right-3 nx-chip !py-1 !px-2.5">
              <Users className="w-3 h-3" /> {stream.viewers.toLocaleString("pt-BR")}
            </div>
          </div>

          {/* Meta */}
          <div className="mt-5 flex flex-col sm:flex-row items-start gap-4">
            <Link to={`/app/criador/${stream.creator}`}>
              <Avatar className="w-14 h-14 ring-2 ring-[hsl(var(--nx-blue))]">
                <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${stream.creator}`} />
                <AvatarFallback className="bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] text-white font-semibold">
                  {stream.creator[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-bold text-xl truncate">{stream.title}</h1>
              <div className="text-sm text-muted-foreground">
                <Link to={`/app/criador/${stream.creator}`} className="hover:text-[hsl(var(--nx-cyan))]">@{stream.creator}</Link> · {stream.game}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {stream.tags.map((t) => <span key={t} className="nx-chip !text-[9px] !py-0.5 !px-2">{t}</span>)}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button className="rounded-xl nx-btn-blue border-0 h-10 px-4 font-semibold" data-testid="stream-follow-btn">
                <Heart className="w-4 h-4 mr-2" /> Seguir
              </Button>
              <Button variant="outline" className="rounded-xl border-[hsl(var(--nx-border))] h-10 hover:bg-white/5" data-testid="stream-subscribe-btn">
                <Gift className="w-4 h-4 mr-2" /> Apoiar
              </Button>
              <Button variant="outline" size="icon" className="rounded-xl border-[hsl(var(--nx-border))] h-10 w-10 hover:bg-white/5" data-testid="stream-share-btn">
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <p className="mt-5 text-sm text-muted-foreground max-w-3xl">{stream.description}</p>

          <div className="mt-6 nx-panel-2 p-4 flex gap-3 text-xs text-[hsl(var(--nx-cyan))]">
            <Radio className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>Demo:</strong> este player reproduz um vídeo de amostra. Streaming real (WebRTC, RTMP, transcodificação) será liberado em fase futura.
            </span>
          </div>
        </div>
      </section>

      {/* Chat lateral */}
      <aside className="w-80 shrink-0 border-l border-[hsl(var(--nx-border))] bg-[hsl(var(--nx-panel))] flex flex-col overflow-hidden hidden lg:flex" data-testid="stream-chat">
        <div className="h-14 shrink-0 border-b border-[hsl(var(--nx-border))] px-4 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Chat da transmissão</div>
          <button className="w-8 h-8 rounded-md hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center"><MoreVertical className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-2">
          {MOCK_CHAT.map((m) => (
            <div key={m.id} className="text-sm px-1 hover:bg-white/[0.03] rounded py-0.5">
              <span className="font-semibold mr-2" style={{ color: m.color }}>{m.user}</span>
              <span className="text-foreground/90">{m.text}</span>
            </div>
          ))}
          <div className="text-center text-[10px] text-muted-foreground pt-6 px-4 leading-relaxed">
            Chat somente-visualização no MVP.<br />Envio em tempo real chegará junto com o streaming real.
          </div>
        </div>
        <div className="p-3 border-t border-[hsl(var(--nx-border))]">
          <div className="flex gap-2 items-center nx-panel-2 rounded-xl px-3 opacity-60">
            <input disabled placeholder="Envio em breve…" className="flex-1 bg-transparent h-10 text-sm outline-none" />
            <button disabled className="w-8 h-8 rounded-lg nx-btn-blue border-0 grid place-items-center"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </aside>
    </main>
  );
}
